import { useCallback, useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import {
  CapabilitiesResponseDataSchema,
  CapabilityCampaignAssignation,
  CapabilityIdentifier,
  CapabilityResponseDataSchema,
  ContactAdditionalField,
  ContactChannel,
} from "schema"
import { toast } from "sonner"
import z from "zod"
import { useInstallCapabilityMutation } from "../../hooks/useInstallCapabilityMutation"
import { useUpdateCapabilityConfigMutation } from "../../hooks/useUpdateCapabilityConfigMutation"
import { BusinessOpportunityDefinitionField } from "./BusinessOpportunityDefinitionField"
import {
  BusinessOpportunityRequiredFieldsField,
  RequiredField,
} from "./BusinessOpportunityRequiredFieldsField"
import { ContactRequirementFields } from "./ContactRequirementFields"

type ContactChannelValue = (typeof ContactChannel)[keyof typeof ContactChannel]

type Props = {
  capability: z.infer<typeof CapabilitiesResponseDataSchema>[number]
  mode?: "install" | "update"
  initialConfig?: {
    definition?: string
    requiredFields?: RequiredField[]
    contactChannel?: ContactChannelValue
    additionalFields?: ContactAdditionalField[]
  }
  onClose: () => void
  onPendingChange?: (isPending: boolean) => void
  onConfigure?: (capability: z.infer<typeof CapabilityResponseDataSchema>) => void
  onSubmitHandlerRef?: React.MutableRefObject<
    | ((
        campaignAssignation: CapabilityCampaignAssignation,
        campaignIds: string[],
        campaignFlow?: "Inbound" | "Outbound",
      ) => Promise<void>)
    | null
  >
}

export function BusinessOpportunityForm({
  capability,
  mode = "install",
  initialConfig,
  onClose,
  onPendingChange,
  onConfigure,
  onSubmitHandlerRef,
}: Props) {
  const { t } = useTranslation()
  const [definition, setDefinition] = useState(initialConfig?.definition ?? "")
  const [requiredFields, setRequiredFields] = useState<RequiredField[]>(
    (initialConfig?.requiredFields ?? []).map((f) => ({
      ...f,
      type: f.type ?? "string",
      required: f.required !== false,
    })),
  )
  const [contactChannel, setContactChannel] = useState<ContactChannelValue>(
    initialConfig?.contactChannel ?? ContactChannel.PhoneOrEmail,
  )
  const [additionalFields, setAdditionalFields] = useState<ContactAdditionalField[]>(
    initialConfig?.additionalFields ?? [],
  )

  const { mutateAsync: installCapability, isPending: isInstallPending } =
    useInstallCapabilityMutation()
  const { mutateAsync: updateCapabilityConfig, isPending: isUpdatePending } =
    useUpdateCapabilityConfigMutation()

  const isPending = mode === "install" ? isInstallPending : isUpdatePending

  useEffect(() => {
    onPendingChange?.(isPending)
  }, [isPending, onPendingChange])

  const handleSubmit = useCallback(
    async (
      campaignAssignation: CapabilityCampaignAssignation,
      campaignIds: string[],
      campaignFlow?: "Inbound" | "Outbound",
    ) => {
      if (isPending) return

      const config = { definition, requiredFields, contactChannel, additionalFields }

      let installedCapability: z.infer<typeof CapabilityResponseDataSchema> | undefined

      if (mode === "install") {
        installedCapability = await installCapability({
          identifier: CapabilityIdentifier.BusinessOpportunity,
          config,
          campaignAssignation,
          campaignIds,
          campaignFlow,
        })

        toast.success(
          t("capabilities.list.install.success", {
            capability: t("capabilities.catalog.BusinessOpportunity.name"),
          }),
        )
      } else {
        await updateCapabilityConfig({ id: capability.id, config })
      }

      if (onConfigure && installedCapability) {
        onConfigure(installedCapability)
      } else {
        onClose()
      }
    },
    [
      isPending,
      mode,
      definition,
      requiredFields,
      contactChannel,
      additionalFields,
      capability.id,
      installCapability,
      updateCapabilityConfig,
      onConfigure,
      onClose,
      t,
    ],
  )

  if (onSubmitHandlerRef) {
    onSubmitHandlerRef.current = handleSubmit
  }

  return (
    <div className="flex flex-col gap-6">
      <BusinessOpportunityDefinitionField value={definition} onChange={setDefinition} />
      <BusinessOpportunityRequiredFieldsField value={requiredFields} onChange={setRequiredFields} />
      <ContactRequirementFields
        value={{ contactChannel, additionalFields }}
        onChange={(contact) => {
          setContactChannel(contact.contactChannel)
          setAdditionalFields(contact.additionalFields)
        }}
      />
    </div>
  )
}
